import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '@/components/SEOHead';
import { Mail, Phone, MessageSquare } from 'lucide-react';
import { MarketingShell, PageHero, PageSection, FaqList, ClosingCta, buildFaqJsonLd, buildBreadcrumb } from '@/components/marketing/MarketingShell';

const FAQ = [
  {
    q: 'Nerava cocok untuk bisnis seperti apa?',
    a: 'Toko online, UMKM, dan tim customer service yang pembelinya datang lewat chat. Mulai dari penjual perabot, fesyen, makanan, sampai produk digital yang pertanyaannya berulang setiap hari.',
  },
  {
    q: 'Apakah saya perlu tim IT untuk memakai Nerava?',
    a: 'Tidak. Seluruh pengaturan dikerjakan dari dashboard: hubungkan channel, unggah katalog, isi SOP, lalu uji di simulator. Tanpa coding dan tanpa server sendiri.',
  },
  {
    q: 'Bagaimana Nerava menjaga data saya?',
    a: 'Data tiap bisnis dipisah dan hanya bisa diakses oleh akun Anda. Channel WhatsApp, Instagram, dan Messenger dihubungkan lewat API resmi Meta.',
  },
  {
    q: 'Bagaimana cara menghubungi tim Nerava?',
    a: 'Lewat email support@nerava.id untuk bantuan teknis, sales@nerava.id untuk kebutuhan tim besar, atau WhatsApp di +62 851-2128-8598 pada jam kerja.',
  },
];

const JSON_LD = [
  buildBreadcrumb('Tentang Kami', '/tentang-kami'),
  buildFaqJsonLd(FAQ),
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': 'https://nerava.id/#organization',
    name: 'Nerava',
    url: 'https://nerava.id',
    logo: 'https://nerava.id/images/logo.png',
    email: 'support@nerava.id',
    telephone: '+62-851-2128-8598',
    areaServed: 'ID',
    contactPoint: [
      { '@type': 'ContactPoint', contactType: 'customer support', email: 'support@nerava.id', telephone: '+62-851-2128-8598', availableLanguage: ['id', 'en'] },
      { '@type': 'ContactPoint', contactType: 'sales', email: 'sales@nerava.id', availableLanguage: ['id', 'en'] },
    ],
  },
];

const CONTACTS = [
  { icon: <Mail className="w-5 h-5" />, label: 'Bantuan teknis', value: 'support@nerava.id', href: 'mailto:support@nerava.id', testid: 'contact-support-email' },
  { icon: <MessageSquare className="w-5 h-5" />, label: 'Kebutuhan tim besar', value: 'sales@nerava.id', href: 'mailto:sales@nerava.id', testid: 'contact-sales-email' },
  { icon: <Phone className="w-5 h-5" />, label: 'WhatsApp jam kerja', value: '+62 851-2128-8598', href: 'tel:+6285121288598', testid: 'contact-phone' },
];

const AboutPage = () => (
  <MarketingShell active="/tentang-kami">
    <SEOHead
      title="Tentang Nerava: Tim di Balik AI Customer Assistant UMKM"
      description="Nerava dibangun untuk toko online dan UMKM Indonesia yang pembelinya datang lewat chat. Kenali misi, cara kerja, dan cara menghubungi tim Nerava."
      canonical="https://nerava.id/tentang-kami"
      jsonLd={JSON_LD}
    />
    <PageHero
      eyebrow="Tentang Kami"
      title="Dibuat untuk penjual yang"
      highlight="jualannya lewat chat"
      subtitle="Nerava lahir dari masalah yang sangat biasa: chat menumpuk, pembeli menunggu, dan pesanan hilang karena telat dibalas. Kami membereskan bagian itu agar penjual bisa fokus ke produknya."
      bullets={['Fokus pasar Indonesia', 'Tanpa coding', 'Didukung channel resmi Meta']}
    />

    <PageSection title="Yang kami percayai" tinted>
      <div className="grid sm:grid-cols-3 gap-5">
        {[
          ['Jawaban harus berdasar data', 'AI hanya boleh menjawab dari katalog, stok, dan SOP yang Anda isi. Lebih baik mengaku tidak tahu daripada mengarang dan mengecewakan pembeli.'],
          ['Penjual tetap pemegang kendali', 'Anda bisa mengambil alih percakapan kapan saja, mengubah gaya bahasa, dan menentukan batas yang tidak boleh dilewati AI.'],
          ['Sederhana itu wajib', 'Setup dikerjakan sendiri dalam hitungan menit. Kalau sebuah fitur butuh tim IT, berarti belum selesai kami rancang.'],
        ].map(([title, desc]) => (
          <div key={title} className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-zinc-800 dark:bg-zinc-900/60">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">{title}</h3>
            <p className="text-sm text-slate-600 dark:text-zinc-400 leading-relaxed">{desc}</p>
          </div>
        ))}
      </div>
    </PageSection>

    <PageSection title="Hubungi kami" intro="Tim kami membalas pada jam kerja, Senin sampai Jumat.">
      <div className="grid sm:grid-cols-3 gap-5" data-testid="contact-list">
        {CONTACTS.map((c) => (
          <a
            key={c.value}
            href={c.href}
            data-testid={c.testid}
            className="rounded-2xl border border-slate-200 bg-white p-6 transition-[transform,border-color] hover:-translate-y-1 hover:border-indigo-300 dark:border-zinc-800 dark:bg-zinc-900/60 dark:hover:border-indigo-500/50"
          >
            <div className="mb-4 grid place-items-center w-11 h-11 rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400">{c.icon}</div>
            <p className="text-xs uppercase tracking-[0.14em] text-slate-500 dark:text-zinc-500 mb-1">{c.label}</p>
            <p className="text-base font-semibold text-slate-900 dark:text-white break-all">{c.value}</p>
          </a>
        ))}
      </div>
      <p className="mt-8 text-sm text-slate-500 dark:text-zinc-400">
        Mau lihat kemampuannya dulu? Mulai dari{' '}
        <Link to="/ai-customer-service" className="text-indigo-600 dark:text-indigo-400 hover:underline" data-testid="link-to-acs-from-about">
          AI Customer Service
        </Link>{' '}
        atau langsung cek{' '}
        <Link to="/harga" className="text-indigo-600 dark:text-indigo-400 hover:underline" data-testid="link-to-pricing-from-about">
          daftar harga
        </Link>
        .
      </p>
    </PageSection>

    <PageSection title="Pertanyaan umum" tinted>
      <FaqList items={FAQ} />
    </PageSection>

    <ClosingCta
      title="Mari mulai dari satu channel"
      subtitle="Aktifkan trial 30 hari, hubungkan WhatsApp atau Instagram, dan rasakan bedanya saat tidak ada chat yang terlambat dibalas."
    />
  </MarketingShell>
);

export default AboutPage;
